// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/it';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import DoctorCalendarService from '../../services/doctor.calendar.service';
import { Clock, Calendar as CalendarIcon } from 'lucide-react';
import PropTypes from 'prop-types';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import TimeSelector from '../../components/common/TimeSelector';

// Imposta la localizzazione di moment in italiano
moment.locale('it');
const localizer = momentLocalizer(moment);

// Traduzione completa per react-big-calendar
const messages = {
  allDay: 'Tutto il giorno',
  previous: 'Precedente',
  next: 'Successivo',
  today: 'Oggi',
  month: 'Mese',
  week: 'Settimana',
  day: 'Giorno',
  agenda: 'Agenda',
  date: 'Data',
  time: 'Ora',
  event: 'Evento',
  showMore: total => `+ Altri ${total}`,
  // Aggiunti i nomi dei mesi in italiano
  january: 'Gennaio',
  february: 'Febbraio',
  march: 'Marzo',
  april: 'Aprile',
  may: 'Maggio',
  june: 'Giugno',
  july: 'Luglio',
  august: 'Agosto',
  september: 'Settembre',
  october: 'Ottobre',
  november: 'Novembre',
  december: 'Dicembre',
  // Aggiunti i nomi dei giorni in italiano
  sunday: 'Domenica',
  monday: 'Lunedì',
  tuesday: 'Martedì',
  wednesday: 'Mercoledì',
  thursday: 'Giovedì',
  friday: 'Venerdì',
  saturday: 'Sabato',
  // Versioni abbreviate
  sun: 'Dom',
  mon: 'Lun',
  tue: 'Mar',
  wed: 'Mer',
  thu: 'Gio',
  fri: 'Ven',
  sat: 'Sab'
};

const DoctorCalendar = ({ therapistId }) => {
  // States
  const [availabilities, setAvailabilities] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isExceptionModalVisible, setIsExceptionModalVisible] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    id: null,
    date: '',
    start_time: '',
    end_time: '',
    slot_duration: 30,
    repeat_weekly: false,
    repeat_until: '',
    notes: ''
  });
  const [exceptionFormData, setExceptionFormData] = useState({
    date: '',
    is_available: false,
    notes: ''
  });

  // Carica i dati iniziali e quando cambia il mese
  useEffect(() => {
    const startDate = moment(calendarDate).startOf('month').format('YYYY-MM-DD');
    const endDate = moment(calendarDate).endOf('month').format('YYYY-MM-DD');
    loadData(startDate, endDate);
  }, [calendarDate, therapistId]);

  // Funzione per caricare i dati
  const loadData = async (startDate, endDate) => {
    setLoading(true);
    setError('');
    try {
      // Carica le disponibilità
      const availabilitiesData = await DoctorCalendarService.getAvailabilities(
        therapistId,
        startDate,
        endDate
      );

      // Carica gli appuntamenti - aggiunto therapistId come primo parametro
      const appointmentsData = await DoctorCalendarService.getAppointments(
        therapistId,
        startDate,
        endDate
      );

      // Trasforma i dati per il calendario
      const formattedAvailabilities = formatAvailabilitiesForCalendar(availabilitiesData.available_slots || []);
      const formattedAppointments = formatAppointmentsForCalendar(appointmentsData || []);

      setAvailabilities(formattedAvailabilities);
      setAppointments(formattedAppointments);
    } catch (err) {
      console.error('Errore nel caricamento dei dati:', err);
      setError('Si è verificato un errore nel caricamento dei dati. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  // Formatta le disponibilità per il calendario
  const formatAvailabilitiesForCalendar = (slots) => {
    return slots.map(slot => ({
      id: `avail-${slot.date}-${slot.start}`,
      title: `Disponibile: ${slot.start} - ${slot.end}`,
      start: new Date(`${slot.date}T${slot.start}`),
      end: new Date(`${slot.date}T${slot.end}`),
      type: 'availability',
      slot: slot
    }));
  };

  // Formatta gli appuntamenti per il calendario
  const formatAppointmentsForCalendar = (appointments) => {
    return appointments.map(appointment => ({
      id: `appt-${appointment.id}`,
      title: `Appuntamento: ${appointment.patient_name || 'Paziente'}`,
      start: new Date(`${appointment.date}T${moment(appointment.time).format('HH:mm')}`),
      end: new Date(`${appointment.date}T${moment(appointment.time).add(appointment.duration || 30, 'minutes').format('HH:mm')}`),
      type: 'appointment',
      appointment: appointment
    }));
  };

  // Gestione del clic su uno slot
  const handleSelectSlot = (slotInfo) => {
    // Imposta valori di default
    const startDate = moment(slotInfo.start).format('YYYY-MM-DD');
    const startTime = moment(slotInfo.start).format('HH:mm');
    const endTime = moment(slotInfo.end).format('HH:mm');

    setFormData({
      id: null,
      date: startDate,
      start_time: startTime,
      end_time: endTime,
      slot_duration: 30,
      repeat_weekly: false,
      repeat_until: '',
      notes: ''
    });

    setSelectedSlot(slotInfo);
    setIsModalVisible(true);
  };

  // Gestione del clic su un evento
  const handleSelectEvent = (event) => {
    if (event.type === 'availability') {
      // Se è un evento di disponibilità, mostra il modal per modificarlo
      const slot = event.slot;
      setFormData({
        id: slot.id,
        date: slot.date,
        start_time: slot.start,
        end_time: slot.end,
        slot_duration: 30,
        repeat_weekly: false,
        repeat_until: '',
        notes: slot.notes || ''
      });

      setSelectedSlot(event);
      setIsModalVisible(true);
    }
    // Per gli appuntamenti, potresti mostrare un altro modal o i dettagli
  };

  // Gestione dell'invio del form
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formattedData = {
        therapist_id: therapistId,
        date: formData.date,
        start_time: formData.start_time,
        end_time: formData.end_time,
        slot_duration: formData.slot_duration,
        is_available: true,
        repeat_weekly: formData.repeat_weekly,
        repeat_until: formData.repeat_weekly && formData.repeat_until ? formData.repeat_until : null,
        notes: formData.notes
      };

      if (selectedSlot && selectedSlot.slot && selectedSlot.slot.id) {
        // Aggiorna disponibilità esistente
        await DoctorCalendarService.updateAvailability(selectedSlot.slot.id, formattedData);
      } else {
        // Crea nuova disponibilità
        await DoctorCalendarService.createAvailability(formattedData);
      }

      // Ricarica i dati
      const startDate = moment(calendarDate).startOf('month').format('YYYY-MM-DD');
      const endDate = moment(calendarDate).endOf('month').format('YYYY-MM-DD');
      loadData(startDate, endDate);

      setIsModalVisible(false);
    } catch (err) {
      console.error('Errore nel salvare la disponibilità:', err);
      setError('Si è verificato un errore nel salvare la disponibilità. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  // Eliminazione di una disponibilità
  const handleDeleteAvailability = async () => {
    if (!selectedSlot || !selectedSlot.slot || !selectedSlot.slot.id) {
      return;
    }

    setLoading(true);
    try {
      await DoctorCalendarService.deleteAvailability(selectedSlot.slot.id);

      // Ricarica i dati
      const startDate = moment(calendarDate).startOf('month').format('YYYY-MM-DD');
      const endDate = moment(calendarDate).endOf('month').format('YYYY-MM-DD');
      loadData(startDate, endDate);

      setIsModalVisible(false);
    } catch (err) {
      console.error('Errore nell\'eliminare la disponibilità:', err);
      setError('Si è verificato un errore nell\'eliminare la disponibilità. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  // Aggiungiamo una nuova funzione per eliminare uno slot specifico
  const handleDeleteSlot = async (slotId) => {
    if (!slotId) {
      return;
    }

    // Conferma prima di eliminare
    if (!window.confirm('Sei sicuro di voler eliminare questo slot? L\'operazione non può essere annullata.')) {
      return;
    }

    setLoading(true);
    try {
      await DoctorCalendarService.deleteAvailability(slotId);

      // Ricarica i dati
      const startDate = moment(calendarDate).startOf('month').format('YYYY-MM-DD');
      const endDate = moment(calendarDate).endOf('month').format('YYYY-MM-DD');
      loadData(startDate, endDate);

      // Chiudi il modale se è aperto
      if (isModalVisible) {
        setIsModalVisible(false);
      }
    } catch (err) {
      console.error('Errore nell\'eliminare lo slot:', err);
      setError('Si è verificato un errore nell\'eliminare lo slot. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  // Apertura del modal per impostare un'eccezione
  const openExceptionModal = () => {
    setExceptionFormData({
      date: selectedSlot ? moment(selectedSlot.start).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
      is_available: false,
      notes: ''
    });

    setIsExceptionModalVisible(true);
  };

  // Gestione dell'invio del form per un'eccezione
  const handleExceptionSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formattedData = {
        therapist_id: therapistId,
        date: exceptionFormData.date,
        is_available: exceptionFormData.is_available,
        notes: exceptionFormData.notes
      };

      await DoctorCalendarService.setException(formattedData);

      // Ricarica i dati
      const startDate = moment(calendarDate).startOf('month').format('YYYY-MM-DD');
      const endDate = moment(calendarDate).endOf('month').format('YYYY-MM-DD');
      loadData(startDate, endDate);

      setIsExceptionModalVisible(false);
    } catch (err) {
      console.error('Errore nell\'impostare l\'eccezione:', err);
      setError('Si è verificato un errore nell\'impostare l\'eccezione. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  // Gestione del cambio di mese/vista
  const handleNavigate = (date) => {
    setCalendarDate(date);
  };

  // Eventi combinati per il calendario
  const calendarEvents = [...availabilities, ...appointments];

  // Personalizzazione dello stile degli eventi
  const eventStyleGetter = (event) => {
    let style = {
      border: '0px',
      borderRadius: '4px',
      opacity: 0.8,
      padding: '2px 5px',
      fontSize: '12px'
    };

    if (event.type === 'availability') {
      style.backgroundColor = '#4CAF50'; // verde per disponibilità
    } else if (event.type === 'appointment') {
      style.backgroundColor = '#F44336'; // rosso per appuntamenti
    }

    return { style };
  };

  // Handler per l'input change del modulo
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handler per l'input change del modulo eccezioni
  const handleExceptionInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setExceptionFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="therapist-calendar-container">
      <div className="calendar-header flex justify-between items-center mb-4">
        <div className="flex items-center">
          <h3 className="text-lg font-medium">Calendario Disponibilità</h3>
        </div>
        <button
          className="px-4 py-2 bg-purple-600 text-white rounded flex items-center"
          onClick={openExceptionModal}
        >
          <CalendarIcon className="w-4 h-4 mr-2" />
          Imposta Giorno Non Disponibile
        </button>
      </div>

      {error && (
        <div className="p-4 mb-4 bg-red-50 border border-red-200 text-red-600 rounded-md">
          {error}
        </div>
      )}

      <Calendar
        localizer={localizer}
        events={calendarEvents}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 700 }}
        selectable
        onSelectSlot={handleSelectSlot}
        onSelectEvent={handleSelectEvent}
        eventPropGetter={eventStyleGetter}
        onNavigate={handleNavigate}
        date={calendarDate}
        views={['month', 'week', 'day']}
        step={30}
        timeslots={2}
        messages={messages} // Contiene tutte le traduzioni
        culture="it" // Imposta la lingua italiana per il calendario
        min={new Date(0, 0, 0, 8, 0)} // Imposta l'ora di inizio alle 8:00
        max={new Date(0, 0, 0, 23, 0)} // Imposta l'ora di fine alle 23:00
      />

      {/* Modal per aggiungere/modificare disponibilità */}
      {isModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {selectedSlot && selectedSlot.slot && selectedSlot.slot.id
                  ? "Modifica Disponibilità"
                  : "Aggiungi Disponibilità"}
              </h3>
              <button
                onClick={() => setIsModalVisible(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleFormSubmit}>
              {/* Prima sezione: Messaggio di modifica se stiamo modificando uno slot esistente */}
              {selectedSlot && selectedSlot.slot && selectedSlot.slot.id && (
                <div className="mb-4 bg-blue-50 p-2 rounded-md">
                  <p className="text-sm text-blue-700">
                    Stai modificando uno slot esistente. ID: {selectedSlot.slot.id}
                  </p>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>

              {/* TimeSelector per orario di inizio */}
              <TimeSelector
                name="start_time"
                value={formData.start_time}
                onChange={handleInputChange}
                label="Orario di inizio"
                minHour={8}  // Mostra orari dalle 8:00
                maxHour={19} // fino alle 19:00
                step={60}    // con intervalli di 60 minuti (1 ora)
              />

              {/* TimeSelector per orario di fine */}
              <TimeSelector
                name="end_time"
                value={formData.end_time}
                onChange={handleInputChange}
                label="Orario di fine"
                minHour={9}  // Mostra orari dalle 9:00
                maxHour={20} // fino alle 20:00
                step={60}    // con intervalli di 60 minuti (1 ora)
              />

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Durata slot (minuti)
                </label>
                <select
                  name="slot_duration"
                  value={formData.slot_duration}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="60">1 ora</option>
                </select>
              </div>

              <div className="mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="repeat_weekly"
                    checked={formData.repeat_weekly}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Ripeti settimanalmente</span>
                </div>
              </div>

              {formData.repeat_weekly && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ripeti fino a (opzionale)
                  </label>
                  <input
                    type="date"
                    name="repeat_until"
                    value={formData.repeat_until}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note (opzionale)
                </label>
                <input
                  type="text"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>

              {/* Sezione dei pulsanti */}
              <div className="flex flex-col space-y-4 mt-6">
                {/* Pulsante Elimina in una sezione separata e ben visibile */}
                {selectedSlot && selectedSlot.slot && selectedSlot.slot.id && (
                  <div className="border-t border-b border-gray-200 py-4 my-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-red-600">Vuoi eliminare questo slot?</span>
                      <button
                        type="button"
                        onClick={() => handleDeleteSlot(selectedSlot.slot.id)}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center"
                        disabled={loading}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        Elimina Slot
                      </button>
                    </div>
                  </div>
                )}

                {/* Pulsanti Annulla e Salva */}
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setIsModalVisible(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md bg-white hover:bg-gray-50"
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                    disabled={loading}
                  >
                    {loading ? 'Salvataggio...' : 'Salva'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal per impostare eccezioni */}
      {isExceptionModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Imposta Giorno Non Disponibile</h3>
              <button
                onClick={() => setIsExceptionModalVisible(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleExceptionSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data
                </label>
                <input
                  type="date"
                  name="date"
                  value={exceptionFormData.date}
                  onChange={handleExceptionInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>

              <div className="mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_available"
                    checked={exceptionFormData.is_available}
                    onChange={handleExceptionInputChange}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Disponibile in questa data</span>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo (opzionale)
                </label>
                <input
                  type="text"
                  name="notes"
                  value={exceptionFormData.notes}
                  onChange={handleExceptionInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="Es. Ferie, impegno personale, etc."
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setIsExceptionModalVisible(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded bg-white hover:bg-gray-50 mr-2"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                  disabled={loading}
                >
                  {loading ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

DoctorCalendar.propTypes = {
  therapistId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]).isRequired
};

export default DoctorCalendar;